import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import 'chartjs-adapter-date-fns';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IInnovationMetricsHistoryPoint } from '../../../../../../core/models/results.model';
import { ICompany } from '../../../../../../core/models/company.model';
import { SIGNAL_COLORS } from '../../../../../../shared/utils/bracket.utils';

type SecondaryAxis = 'innovation_score' | 'invention_count_120m' | 'relative_growth_12m' | 'quality_score_12m';
type TimeRange = '10y' | '3y' | '1y';

interface SecondaryAxisCfg {
  label: string;
  field: keyof IInnovationMetricsHistoryPoint;
  color: string;
  type: 'scatter' | 'bar';
  hoverSuffix: string;
  opacity?: number;
}

const SECONDARY_AXIS_CONFIG: Record<SecondaryAxis, SecondaryAxisCfg> = {
  innovation_score:     { label: 'Innovation Score',    field: 'innovation_score',     color: '#3b82f6', type: 'scatter', hoverSuffix: '' },
  invention_count_120m: { label: 'Portfolio Size (10y)', field: 'invention_count_120m', color: '#22c55e', type: 'bar',     opacity: 0.4,  hoverSuffix: '' },
  relative_growth_12m:  { label: 'Growth (%)',           field: 'relative_growth_12m',  color: '#f59e0b', type: 'bar',     opacity: 0.45, hoverSuffix: '%' },
  quality_score_12m:    { label: 'Quality Score',        field: 'quality_score_12m',    color: '#a855f7', type: 'scatter', hoverSuffix: '' },
};

const FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const COLOR = {
  axisText: '#64748b',
  axisTitle: '#64748b',
  tooltipBg: '#ffffff',
  tooltipTitle: '#64748b',
  tooltipBody: '#0f172a',
  tooltipBorder: 'rgba(15, 23, 42, 0.08)',
  grid: 'rgba(15, 23, 42, 0.05)',
  chartAreaBg: '#f8fafc',
  scoreLine: '#1e293b',
};

const whiteBackgroundPlugin = {
  id: 'innovSignalWhiteBg',
  beforeDraw(chart: Chart) {
    const ctx = chart.ctx;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

const chartAreaBackgroundPlugin = {
  id: 'innovSignalChartAreaBg',
  beforeDatasetsDraw(chart: Chart) {
    const { left, top, width, height } = chart.chartArea;
    const ctx = chart.ctx;
    ctx.save();
    ctx.fillStyle = COLOR.chartAreaBg;
    ctx.fillRect(left, top, width, height);
    ctx.restore();
  },
};

Chart.register(...registerables, zoomPlugin, annotationPlugin);

const DAY_MS = 86_400_000;
const MIN_ZOOM_RANGE_MS = 30 * DAY_MS;
const AXIS_PAD_DAYS = 10;

const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const addDays = (d: Date, days: number): Date => {
  const c = new Date(d);
  c.setDate(c.getDate() + days);
  return c;
};

// Bars/markers are positioned at the 1st of the month so they align with
// month-start ticks instead of sitting one day before the next month's tick
// (date_eom '2026-03-31' would otherwise render under the 'Apr 26' label).
const toMonthStart = (dateEom: string): string => `${dateEom.slice(0, 7)}-01`;

@Component({
  selector: 'app-innovation-metrics-activity',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BaseChartDirective],
  templateUrl: './innovation-metrics-activity.component.html',
  styleUrl: './innovation-metrics-activity.component.css',
})
export class InnovationMetricsActivityComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() entity_data: ICompany | null = null;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  history: IInnovationMetricsHistoryPoint[] = [];

  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBackgroundPlugin];

  showChart = false;
  primaryLabel = 'Stock Performance (Indexed)';
  hasMcap = true;

  secondaryAxis: SecondaryAxis | null = 'innovation_score';
  timeRange: TimeRange = '10y';

  readonly SIGNAL = SIGNAL_COLORS;
  readonly SECONDARY_CFG = SECONDARY_AXIS_CONFIG;

  showSpinner = false;
  showDataMissing = false;
  loadingFailed = false;
  isUnsupportedCompanyType = false;

  private readonly SUPPORTED_COMPANY_TYPES = ['Public Company', 'Public Investment Firm'];

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.reset();
      this.loadHistory();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get chartCardTitle(): string {
    return this.secondaryAxis
      ? `Stock Performance vs ${SECONDARY_AXIS_CONFIG[this.secondaryAxis].label}`
      : 'Stock Performance vs Innovation Signal';
  }

  private reset(): void {
    this.history = [];
    this.hasMcap = true;
    this.showChart = false;
    this.showSpinner = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
    this.isUnsupportedCompanyType = false;
  }

  private loadHistory(): void {
    const entityid = this.entityids?.[0];
    if (!entityid) {
      this.showDataMissing = true;
      return;
    }

    const companyType = this.entity_data?.companytype;
    if (companyType && !this.SUPPORTED_COMPANY_TYPES.includes(companyType)) {
      this.isUnsupportedCompanyType = true;
      return;
    }

    const currentTabId = this.tabId;
    this.showSpinner = true;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innov_metrics_history')).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.history = cached;
            this.hasMcap = cached.some((p: IInnovationMetricsHistoryPoint) => p.stockprice_indexed > 0);
            this.buildChart();
            this.showSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getInnovationHistory(entityid!, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            if (!res.ok || !payload?.history?.length) {
              if (this.tabId === currentTabId) {
                this.showSpinner = false;
                this.showDataMissing = true;
              }
              return;
            }
            const targetTabId = payload.tabId ?? currentTabId;
            const historyData = payload.history;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innov_metrics_history', data: historyData }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showSpinner = false;
              this.history = historyData;
              this.hasMcap = historyData.some(p => p.stockprice_indexed > 0);
              this.buildChart();
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  toggleSecondaryAxis(axis: SecondaryAxis): void {
    this.secondaryAxis = this.secondaryAxis === axis ? null : axis;
    if (this.history.length) this.buildChart();
  }

  setTimeRange(range: TimeRange): void {
    this.timeRange = range;
    if (this.history.length) this.buildChart();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'signal-history').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-signal-history.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getFilteredHistory(): IInnovationMetricsHistoryPoint[] {
    if (!this.history.length) return this.history;

    let data = this.history;

    if (this.timeRange === '1y') {
      const lastDate = new Date(data[data.length - 1].date_eom);
      const cutoff = new Date(lastDate);
      cutoff.setFullYear(cutoff.getFullYear() - 1);
      return data.filter(p => new Date(p.date_eom) >= cutoff);
    }

    // 3Y + 10Y: quarter-end points only (source data mixes monthly and quarterly,
    // so this guarantees uniform spacing). Always keep the very last point even
    // if it's a non-quarter month — that's the "live" data tip.
    if (this.timeRange === '3y') {
      const lastDate = new Date(data[data.length - 1].date_eom);
      const cutoff = new Date(lastDate);
      cutoff.setFullYear(cutoff.getFullYear() - 3);
      data = data.filter(p => new Date(p.date_eom) >= cutoff);
    }

    const QUARTER_MONTHS = new Set([3, 6, 9, 12]);
    const last = data[data.length - 1];
    const quarterly = data.filter(p => QUARTER_MONTHS.has(new Date(p.date_eom).getMonth() + 1));
    const lastMonth = new Date(last.date_eom).getMonth() + 1;
    if (!QUARTER_MONTHS.has(lastMonth)) quarterly.push(last);
    return quarterly;
  }

  private buildChart(): void {
    const filtered = this.getFilteredHistory();
    if (!filtered.length) {
      this.showDataMissing = true;
      return;
    }

    this.primaryLabel = 'Stock Performance (Indexed)';
    const primaryPoints = filtered.map(p => ({
      x: toMonthStart(p.date_eom),
      y: p.stockprice_indexed,
      signal: p.signal,
    }));

    const datasets: any[] = [];

    if (this.secondaryAxis) {
      const cfg = SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const secPoints = filtered.map(p => ({
        x: toMonthStart(p.date_eom),
        y: (p[cfg.field] as number | null) ?? null,
      }));

      if (cfg.type === 'bar') {
        const barCount = Math.max(filtered.length, 1);
        const dynamicBarGap = Math.min(0.75, Math.max(0.35, 0.82 - barCount * 0.035));
        datasets.push({
          type: 'bar',
          label: cfg.label,
          data: secPoints,
          yAxisID: 'y2',
          backgroundColor: this.hexWithAlpha(cfg.color, cfg.opacity ?? 0.45),
          borderColor: cfg.color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          categoryPercentage: 0.85,
          barPercentage: Math.max(0.25, 1 - dynamicBarGap),
          order: 3,
        });
      } else {
        datasets.push({
          type: 'line',
          label: cfg.label,
          data: secPoints,
          yAxisID: 'y2',
          borderColor: cfg.color,
          borderWidth: 2,
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: cfg.color,
          fill: false,
          order: 3,
        });
      }
    }

    const radiusBySignal = (sig: string | undefined): number => {
      if (sig === 'BUY' || sig === 'SELL') return 7;
      if (sig === 'HOLD') return 4;
      return 0;
    };

    datasets.push({
      type: 'line',
      label: this.primaryLabel,
      data: primaryPoints,
      yAxisID: 'y',
      borderColor: COLOR.scoreLine,
      borderWidth: 2,
      tension: 0.35,
      cubicInterpolationMode: 'monotone',
      pointRadius: (ctx: any) => radiusBySignal(ctx.raw?.signal),
      pointHoverRadius: (ctx: any) => radiusBySignal(ctx.raw?.signal) + 1,
      pointStyle: (ctx: any) => (ctx.raw?.signal === 'HOLD' ? 'circle' : 'triangle'),
      pointRotation: (ctx: any) => (ctx.raw?.signal === 'SELL' ? 180 : 0),
      pointBackgroundColor: (ctx: any) => {
        const sig = ctx.raw?.signal;
        if (sig === 'BUY') return SIGNAL_COLORS.BUY;
        if (sig === 'HOLD') return SIGNAL_COLORS.HOLD;
        if (sig === 'SELL') return SIGNAL_COLORS.SELL;
        return COLOR.scoreLine;
      },
      pointBorderColor: '#ffffff',
      pointBorderWidth: (ctx: any) => (ctx.raw?.signal ? 1 : 0),
      backgroundColor: 'rgba(30, 41, 59, 0.04)',
      fill: 'origin',
      spanGaps: true,
      order: 2,
    });

    this.chartjsData = { datasets } as any;

    const firstDate = new Date(toMonthStart(filtered[0].date_eom));
    const lastDate = new Date(toMonthStart(filtered[filtered.length - 1].date_eom));
    const xMinStr = isoDay(addDays(firstDate, -AXIS_PAD_DAYS));
    const xMaxStr = isoDay(addDays(lastDate, AXIS_PAD_DAYS));
    const xMinMs = new Date(xMinStr).getTime();
    const xMaxMs = new Date(xMaxStr).getTime();

    const yScale: any = {
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: this.primaryLabel,
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        font: { family: FONT_FAMILY, size: 14 },
        color: COLOR.axisText,
        padding: 8,
      },
      grid: { color: COLOR.grid },
      border: { display: false },
    };

    const scales: any = {
      x: {
        type: 'time',
        min: xMinStr,
        max: xMaxStr,
        offset: false,
        time: {
          unit: this.timeRange === '1y' ? 'month' : 'year',
          tooltipFormat: 'MMM yyyy',
          displayFormats: { month: 'MMM yy', quarter: 'QQQ yyyy', year: 'yyyy' },
        },
        ticks: {
          maxRotation: 0,
          font: { family: FONT_FAMILY, size: 14 },
          color: COLOR.axisText,
          padding: 8,
        },
        grid: { display: false },
        border: { display: false },
      },
      y: yScale,
    };

    if (this.secondaryAxis) {
      const cfg = SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const isBounded = this.secondaryAxis === 'innovation_score' || this.secondaryAxis === 'quality_score_12m';
      scales.y2 = {
        position: 'right',
        min: isBounded ? 0 : undefined,
        max: isBounded ? 100 : undefined,
        title: {
          display: true,
          text: cfg.label,
          font: { family: FONT_FAMILY, size: 14, weight: 500 },
          color: cfg.color,
          padding: { bottom: 8 },
        },
        ticks: {
          maxTicksLimit: 6,
          font: { family: FONT_FAMILY, size: 14 },
          color: cfg.color,
          padding: 8,
        },
        grid: { display: false },
        border: { display: false },
      };
    }

    const annotations: any = {};

    this.chartjsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: 24, right: 32, bottom: 8, left: 8 } },
      animation: {
        duration: 800,
        easing: 'easeOutQuart',
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: COLOR.tooltipBg,
          titleColor: COLOR.tooltipTitle,
          titleFont: { family: FONT_FAMILY, size: 13, weight: 500 },
          bodyColor: COLOR.tooltipBody,
          bodyFont: { family: FONT_FAMILY, size: 14, weight: 600 },
          padding: 12,
          borderColor: COLOR.tooltipBorder,
          borderWidth: 1,
          displayColors: true,
          cornerRadius: 8,
          caretSize: 0,
          callbacks: {
            title: (items: any[]) => {
              const x = items[0].parsed.x ?? 0;
              return new Date(x).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
            },
            label: (item: any) => {
              const v = item.parsed.y;
              if (v == null) return '';
              const dsLabel = item.dataset.label ?? '';
              if (dsLabel === this.primaryLabel) {
                return ` ${dsLabel}: ${Math.round(v)}`;
              }
              const cfg = this.secondaryAxis ? SECONDARY_AXIS_CONFIG[this.secondaryAxis] : null;
              const suffix = cfg?.hoverSuffix ?? '';
              return ` ${dsLabel}: ${Math.round(v)}${suffix}`;
            },
            afterBody: (items: any[]) => {
              const primary = items.find((i: any) => i.dataset?.label === this.primaryLabel);
              const sig = primary?.raw?.signal;
              if (sig !== 'BUY' && sig !== 'HOLD' && sig !== 'SELL') return '';
              const glyph = sig === 'BUY' ? '▲' : sig === 'SELL' ? '▼' : '●';
              return `${glyph} Signal: ${sig}`;
            },
          },
        },
        annotation: { annotations },
        zoom: {
          limits: { x: { min: xMinMs, max: xMaxMs, minRange: MIN_ZOOM_RANGE_MS } },
          pan: {
            enabled: true,
            mode: 'x',
            modifierKey: 'shift',
          },
          zoom: {
            wheel: { enabled: true },
            pinch: { enabled: true },
            drag: {
              enabled: true,
              backgroundColor: 'rgba(99, 102, 241, 0.15)',
              borderColor: 'rgba(99, 102, 241, 0.6)',
              borderWidth: 1,
            },
            mode: 'x',
          },
        },
      } as any,
      scales,
    };

    this.showChart = true;
  }

  private hexWithAlpha(hex: string, alpha: number): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
